import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { BookOpen, CheckCircle2, BarChart2, Play, Clock, PlayCircle, FileText } from 'lucide-react';
import { testService } from '@/services/testService';
import { Test as TestType } from '@/types/test';

const TestListPage: React.FC = () => {
  const [tests, setTests] = useState<TestType[]>([]);
  const [filteredTests, setFilteredTests] = useState<TestType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExamType, setSelectedExamType] = useState<string>('');
  const [examTypes, setExamTypes] = useState<string[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const fetchTests = async () => {
      try {
        // Use testService which has mock data fallback
        const testsData = await testService.getStudentTests();
        setTests(testsData);

        // Extract unique exam types (using subject as examType for now)
        const uniqueExamTypes = Array.from(
          new Set(testsData.map((test: TestType) => test.subject).filter(Boolean))
        ) as string[];
        setExamTypes(uniqueExamTypes);
      } catch (error) {
        console.error('Error fetching tests:', error);
        toast({
          title: 'Error',
          description: 'Failed to load tests. Please try again later.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTests();
  }, [toast]);

  // Filter tests when selectedExamType changes
  useEffect(() => {
    if (selectedExamType) {
      setFilteredTests(tests.filter(test => test.subject === selectedExamType));
    } else {
      setFilteredTests(tests);
    }
  }, [selectedExamType, tests]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-lg">Loading tests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-foreground font-heading">Available Tests</h1>
        <div className="flex gap-4">
          <select
            className="px-4 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-card text-foreground font-medium font-body"
            value={selectedExamType}
            onChange={(e) => setSelectedExamType(e.target.value)}
          >
            <option value="">All Subjects</option>
            {examTypes.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filteredTests.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-10 h-10 text-primary/60" aria-hidden="true" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-4 font-heading">No tests available</h2>
          <p className="text-muted-foreground font-medium mb-6 font-body">
            {selectedExamType
              ? `There are currently no tests available for ${selectedExamType}. Please select a different subject or check back later.`
              : 'There are currently no tests available. Please check back later.'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTests.map((test) => (
            <Card key={test.id} className="h-full flex flex-col bg-card border border-border transition-all duration-200 hover:scale-105">
              <CardHeader className="bg-primary/10 rounded-t-xl">
                <CardTitle className="text-foreground font-bold font-heading">{test.title}</CardTitle>
                <CardDescription className="text-muted-foreground font-medium font-body">
                  {test.subject} • {test.status === 'completed' ? 'Completed' : test.canStartTest || test.canViewInstructions ? 'Available Now' : test.status === 'upcoming' ? 'Upcoming' : 'Not Available'}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow p-6">
                <p className="mb-4 text-foreground font-body">{test.description}</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center">
                    <span className="font-semibold text-foreground font-body">Questions:</span>
                    <span className="ml-2 text-muted-foreground">{test.totalQuestions}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold text-foreground font-body">Duration:</span>
                    <span className="ml-2 text-muted-foreground">{test.duration} min</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="p-6">
                {test.status === 'completed' ? (
                  <div className="w-full space-y-2">
                    <Button
                      disabled
                      className="w-full bg-gray-400 text-gray-600 cursor-not-allowed py-3 rounded-xl"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" aria-hidden="true" />Completed
                    </Button>
                    <Link to={`/results/${test.id}`} className="w-full">
                      <Button
                        variant="outline"
                        className="w-full border-primary text-primary hover:bg-primary/10 py-2 rounded-xl"
                      >
                        <BarChart2 className="w-4 h-4 mr-2" aria-hidden="true" />View Results
                      </Button>
                    </Link>
                  </div>
                ) : test.status === 'in-progress' || test.canResume ? (
                  <Link to={`/test/${test.id}`} className="w-full">
                    <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-xl transition-all duration-200 transform hover:scale-105">
                      <Play className="w-4 h-4 mr-2" aria-hidden="true" />Resume Test
                    </Button>
                  </Link>
                ) : test.status === 'expired' ? (
                  <Button
                    disabled
                    className="w-full bg-red-100 text-red-600 cursor-not-allowed py-3 rounded-xl"
                  >
                    <Clock className="w-4 h-4 mr-2" aria-hidden="true" />Schedule Expired
                  </Button>
                ) : test.canStartTest ? (
                  <Link to={`/test/${test.id}/instructions`} className="w-full">
                    <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-3 rounded-xl transition-all duration-200 transform hover:scale-105">
                      <PlayCircle className="w-4 h-4 mr-2" aria-hidden="true" />Start Test
                    </Button>
                  </Link>
                ) : test.canViewInstructions ? (
                  <Link to={`/test/${test.id}/instructions`} className="w-full">
                    <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl transition-all duration-200 transform hover:scale-105">
                      <FileText className="w-4 h-4 mr-2" aria-hidden="true" />View Instructions
                    </Button>
                  </Link>
                ) : (
                  <Button
                    disabled
                    className="w-full bg-gray-400 text-gray-600 cursor-not-allowed py-3 rounded-xl"
                  >
                    Not Available
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestListPage;
